import { useState, useEffect, useRef, useCallback } from 'react';
import { useLogoSrc } from '@/hooks/useLogoSrc';
import { copyToClipboard } from '@/lib/clipboard';
import {
  InAppBrowserName,
  Platform,
  getShareableUrl,
  openInExternalBrowser,
  getBrowserDisplayName,
  generateTransferUrl,
  TRANSFER_TOKEN_EXPIRY_SECONDS,
} from '@/lib/in-app-browser';
import { IOSInstructions } from './IOSInstructions';
import { AndroidInstructions } from './AndroidInstructions';

interface InAppBrowserOverlayProps {
  browserName: InAppBrowserName | null;
  platform: Platform;
  onDismiss?: () => void;
}

export function InAppBrowserOverlay({
  browserName,
  platform,
  onDismiss,
}: InAppBrowserOverlayProps) {
  const logoSrc = useLogoSrc();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [isGenerating, setIsGenerating] = useState(true); // Start as generating
  const [isCopying, setIsCopying] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  // Shows when token generation failed - user will need to log in again
  const [tokenFallbackUsed, setTokenFallbackUsed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Track if we've already started generating to prevent double-generation
  const hasStartedGenerating = useRef(false);

  // Ensure overlay starts scrolled to top
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, []);

  // Helper to remove token from URL
  const clearTokenFromUrl = useCallback(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('_t')) {
        url.searchParams.delete('_t');
        window.history.replaceState({}, '', url.pathname + url.search || '/');
      }
    } catch (e) {
      // Ignore errors
    }
  }, []);

  // Cleanup countdown interval and URL token on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      // Clean up URL when overlay unmounts
      clearTokenFromUrl();
    };
  }, [clearTokenFromUrl]);

  // Start countdown timer
  const startCountdown = useCallback((seconds: number) => {
    // Clear any existing countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    setCountdown(seconds);

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          // Countdown finished - regenerate URL
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          setCopied(false);
          // Trigger regeneration
          regenerateUrl();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Generate/regenerate the transfer URL
  const regenerateUrl = useCallback(async () => {
    setIsGenerating(true);
    setTokenFallbackUsed(false);
    setCopied(false);

    try {
      const result = await generateTransferUrl();
      setGeneratedUrl(result.url);
      startCountdown(result.expiresIn);

      // Update the current browser URL with the token
      // This way, if user uses the in-app browser's built-in "Open in Safari" menu,
      // the URL will already have the token for automatic login
      try {
        const urlWithToken = new URL(result.url);
        window.history.replaceState({}, '', urlWithToken.pathname + urlWithToken.search);
      } catch (e) {
        // Ignore URL update errors
        console.warn('Failed to update URL with token:', e);
      }
    } catch (error) {
      console.error('Failed to generate transfer token:', error);
      // Fallback to URL without token
      const url = getShareableUrl();
      setGeneratedUrl(url);
      setTokenFallbackUsed(true);
      // No countdown for fallback URL
    } finally {
      setIsGenerating(false);
    }
  }, [startCountdown]);

  // Pre-generate the URL when overlay loads
  useEffect(() => {
    if (hasStartedGenerating.current) return;
    hasStartedGenerating.current = true;
    regenerateUrl();
  }, [regenerateUrl]);

  // Format countdown for display (MM:SS)
  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCopyLink = async () => {
    if (!generatedUrl || isCopying) return;

    setIsCopying(true);
    setCopyError(false);

    try {
      const success = await copyToClipboard(generatedUrl);

      if (success) {
        setCopied(true);
        // Reset copied state after 3 seconds (visual feedback)
        setTimeout(() => setCopied(false), 3000);
      } else {
        setCopyError(true);
      }
    } catch (error) {
      console.error('Failed to copy link:', error);
      setCopyError(true);
    } finally {
      setIsCopying(false);
    }
  };

  const handleOpenInBrowser = () => {
    if (!generatedUrl) return;
    openInExternalBrowser(generatedUrl);
  };

  const displayName = getBrowserDisplayName(browserName);
  const displayNameWithBrowser =
    browserName && browserName !== 'Unknown'
      ? `${displayName} browser`
      : 'this browser';
  // Determine if buttons should be disabled
  const isLoading = isGenerating || isCopying;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-card flex flex-col items-center justify-start px-6 py-8 overflow-y-auto"
    >
      {/* Spacer to center content vertically but allow scroll */}
      <div className="flex-1 min-h-8" />

      {/* Logo - fixed aspect ratio */}
      <div className="relative mb-8 flex-shrink-0">
        <div
          className="absolute w-32 h-32 -left-4 -top-4 bg-amber-400/20 rounded-full blur-2xl"
          aria-hidden="true"
        />
        <img
          src={logoSrc}
          alt="Gold Machine"
          width={96}
          height={96}
          className="w-24 h-24 relative z-10 object-contain"
        />
      </div>

      {/* Heading */}
      <h1 className="text-2xl font-bold text-white text-center mb-2">
        Open in Safari or Chrome
      </h1>

      <p className="text-muted-foreground text-center mb-6 max-w-xs">
        You're viewing this in {displayNameWithBrowser}. For the best
        experience, open Gold Machine in Safari or Chrome.
      </p>

      {/* Why message */}
      <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6 max-w-xs w-full">
        <p className="text-sm text-foreground/80 text-center">
          Payments, games, and other features require a full browser to work
          properly.
        </p>
      </div>

      {/* Continue anyway option - placed prominently before instructions */}
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="mb-6 text-muted-foreground hover:text-foreground/80 text-sm py-2 px-4 border border-border hover:border-border/80 rounded-lg transition-colors"
        >
          Continue anyway (some features may not work)
        </button>
      )}

      {/* Platform-specific instructions */}
      {platform === 'ios' ? (
        <IOSInstructions
          browserName={browserName}
          onCopyLink={handleCopyLink}
          copied={copied}
          copyError={copyError}
          isLoading={isLoading}
          countdown={countdown}
          formatCountdown={formatCountdown}
          tokenFallbackUsed={tokenFallbackUsed}
        />
      ) : (
        <AndroidInstructions
          onOpenInBrowser={handleOpenInBrowser}
          onCopyLink={handleCopyLink}
          copied={copied}
          copyError={copyError}
          isLoading={isLoading}
          countdown={countdown}
          formatCountdown={formatCountdown}
          tokenFallbackUsed={tokenFallbackUsed}
        />
      )}

      {/* Session transfer fallback warning */}
      {tokenFallbackUsed && (
        <div className="mt-4 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg max-w-xs w-full">
          <p className="text-xs text-amber-300 text-center">
            You'll need to log in again in Safari or Chrome.
            Your session couldn't be transferred automatically.
          </p>
        </div>
      )}

      {/* URL display for manual copy */}
      {copyError && (
        <div className="mt-4 p-3 bg-muted rounded-lg max-w-xs w-full">
          <p className="text-xs text-muted-foreground mb-1">
            Copy this link manually:
          </p>
          <p className="text-xs text-amber-400 break-all font-mono">
            {generatedUrl || getShareableUrl()}
          </p>
        </div>
      )}

      {/* Bottom spacer to center content */}
      <div className="flex-1 min-h-8" />
    </div>
  );
}

export default InAppBrowserOverlay;
